import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";

interface ConfirmRequest {
  text: ReactNode;
  title?: ReactNode;
  // Промис, а не void: диалог ждёт его и держит полосу прогресса, пока
  // запрос идёт. Синхронный обработчик тоже подойдёт, await его пропустит.
  onConfirm: () => Promise<unknown> | unknown;
}

/**
 * Подтверждение массового действия нашим диалогом вместо `window.confirm`.
 *
 * Чем это лучше нативного окна: оно не умеет показывать ход выполнения, и
 * после «ОК» пользователь оставался наедине с молчащим списком. Наш
 * `ConfirmDialog` уже умеет `loading`, а `DialogHeader` рисует по нему
 * полосу прогресса, поэтому диалог остаётся открытым, пока запрос идёт, и
 * закрывается сам по успеху.
 *
 * Ошибку показываем в самом диалоге, а не только снекбаром: снекбар живёт
 * несколько секунд и пропадает, а здесь текст остаётся перед глазами, и
 * повторить действие можно сразу, не выбирая строки заново.
 */
export function useConfirmAction() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string } | null>(null);

  const confirm = useCallback((next: ConfirmRequest) => {
    setError(null);
    setRequest(next);
  }, []);

  const close = useCallback(() => {
    // Закрыть на середине запроса нельзя: ответ всё равно придёт, а
    // пользователь решит, что действие отменено.
    if (loading) return;
    setRequest(null);
    setError(null);
  }, [loading]);

  const handleConfirm = useCallback(async () => {
    if (!request) return;
    setLoading(true);
    setError(null);
    try {
      await request.onConfirm();
      setRequest(null);
    } catch (e) {
      setError({ message: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }, [request]);

  const dialog = (
    <ConfirmDialog
      open={Boolean(request)}
      onCancel={close}
      onConfirm={handleConfirm}
      confirmTitle={request?.title}
      confirmText={request?.text}
      loading={loading}
      error={error ?? undefined}
    />
  );

  return { confirm, dialog, loading, open: Boolean(request) };
}
